/* global google */
import 'normalize.css/normalize.css';
import '../css/index.scss';
import { Loader } from '@googlemaps/js-api-loader';
import { clearText } from './utils';

const locationContainer = document.getElementById('location_container');
const locationSearch = document.getElementById('location_search');
const categoryContainer = document.getElementById('category_container');
const backButton = document.getElementById('back_button');
const backToLocationButton = document.getElementById('back_to_location_button');
const analysisContainer = document.getElementById('analysis_container');
const quoteFormContainer = document.getElementById('quote_form_container');
const submissionSuccessContainer = document.getElementById('submission_success_container');
const backToQuestionStepOneButton = document.getElementById('back_to_question_step_1_button');
const backToQuestionStepTwoButton = document.getElementById('back_to_question_step_2_button');
const quoteForm = document.getElementById('quote_form');
const quoteFormStatus = document.getElementById('quote_form_status');
const firstNameInput = document.getElementById('first_name');
const lastNameInput = document.getElementById('last_name');
const phoneNumberInput = document.getElementById('phone_number');
const emailAddressInput = document.getElementById('email_address');
const submitQuoteButton = document.getElementById('submit_quote_button');
const wizardProgress = document.getElementById('wizard_progress');
const wizardProgressLabel = document.getElementById('wizard_progress_label');
const wizardProgressFill = document.getElementById('wizard_progress_fill');
const wizardShell = document.querySelector('.wizard-shell');
const analysisItems = Array.from(document.querySelectorAll('[data-analysis-item]'));

const params = new URLSearchParams(window.location.search);
const proLanding = params.get('proLanding');
const remodelQuoteApiBaseUrl = process.env.REMODEL_QUOTE_API_BASE_URL
  || 'https://quick-remodel-quote-api-4ffdbd276a94.herokuapp.com/';
const remodelQuoteSubmissionUrl = new URL('api/RemodelQuoteSubmissions', remodelQuoteApiBaseUrl).toString();
let selectedCategory = proLanding;
let selectedSearch;
let selectedCity;
let selectedState;
let selectedQuestionAnswers = [];
let isSubmittingQuote = false;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STEP_TRANSITION_MS = 1000;
const WIZARD_PROGRESS_TOTAL_STEPS = 4;
const ANALYSIS_ITEM_DELAY_MS = 750;
const ANALYSIS_FINAL_DELAY_MS = 950;
const questionSteps = [
  {
    container: document.getElementById('question_step_1_container'),
    heading: document.getElementById('question_step_1_heading'),
    buttonContainer: document.getElementById('question_step_1_button_container'),
  },
  {
    container: document.getElementById('question_step_2_container'),
    heading: document.getElementById('question_step_2_heading'),
    buttonContainer: document.getElementById('question_step_2_button_container'),
  },
  {
    container: document.getElementById('question_step_3_container'),
    heading: document.getElementById('question_step_3_heading'),
    buttonContainer: document.getElementById('question_step_3_button_container'),
  },
];
const postLocationContainers = [
  ...questionSteps.map((step) => step.container),
  locationContainer,
  analysisContainer,
  quoteFormContainer,
  submissionSuccessContainer,
];
const LOCATION_STEP_INDEX = questionSteps.length;
const ANALYSIS_STEP_INDEX = LOCATION_STEP_INDEX + 1;
const QUOTE_FORM_STEP_INDEX = ANALYSIS_STEP_INDEX + 1;
const SUBMISSION_SUCCESS_STEP_INDEX = QUOTE_FORM_STEP_INDEX + 1;
const LAST_PROGRESS_STEP_INDEX = LOCATION_STEP_INDEX;

function buildQuestion(key, question, options, includeNotSure = true) {
  const optionLabels = !includeNotSure || options.includes('I\'m not sure')
    ? options
    : [...options, 'I\'m not sure'];

  return {
    key,
    question,
    options: optionLabels.map((label) => ({
      label,
      value: label
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    })),
  };
}

const CATEGORY_QUESTION_CONFIG = {
  BathroomRemodeling: [
    buildQuestion('bathroomSize', 'What size is your bathroom?', [
      'Small (under 40 sq ft)',
      'Medium (40\u201380 sq ft)',
      'Large (80+ sq ft)',
    ]),
    buildQuestion('layoutPlan', 'Will the layout stay the same?', [
      'Keep existing layout',
      'Minor changes',
      'Complete redesign',
    ]),
    buildQuestion('tileWork', 'How much tile work are you planning?', [
      'Shower area only',
      'Half walls',
      'Floor to ceiling tile',
    ]),
  ],
  KitchenRemodeling: [
    buildQuestion('kitchenSize', 'What size is your kitchen?', [
      'Small (under 100 sq ft)',
      'Medium (100\u2013200 sq ft)',
      'Large (200+ sq ft)',
    ]),
    buildQuestion('cabinetPlan', 'Are you replacing cabinets?', [
      'Yes, new cabinets',
      'Reface existing cabinets',
      'Not sure yet',
    ], false),
    buildQuestion('layoutChange', 'Will the kitchen layout change?', [
      'Keep same layout',
      'Minor changes',
      'Full redesign',
    ]),
  ],
  BasementFinishing: [
    buildQuestion('basementSize', 'How large is the basement area?', [
      'Under 500 sq ft',
      '500\u20131000 sq ft',
      '1000+ sq ft',
    ]),
    buildQuestion('basementCondition', 'What condition is the basement in?', [
      'Unfinished',
      'Partially finished',
      'Already finished but remodeling',
    ], false),
    buildQuestion('basementUse', 'How will you use the basement?', [
      'Living or family space',
      'Home office or gym',
      'Guest suite or apartment',
    ]),
  ],
  PatioExterior: [
    buildQuestion('projectType', 'Which project are you planning?', [
      'Sunroom',
      'Deck',
      'Patio',
    ]),
    buildQuestion('projectSize', 'What size is the project?', [
      'Small',
      'Medium',
      'Large',
    ]),
    buildQuestion('projectUpgrades', 'Are you including any upgrades or extras?', [
      'No, basic project only',
      'Some upgrades',
      'Yes, multiple upgrades / custom features',
    ]),
  ],
  WholeHomeRemodeling: [
    buildQuestion('homeSize', 'What is the size of your home?', [
      'Under 1500 sq ft',
      '1500\u20133000 sq ft',
      '3000+ sq ft',
    ]),
    buildQuestion('remodelType', 'What type of remodel are you planning?', [
      'Several rooms',
      'Most of the house',
      'Full renovation',
    ], false),
    buildQuestion('layoutPlan', 'Will you move walls or change the layout?', [
      'No layout changes',
      'Some layout changes',
      'Major redesign',
    ]),
  ],
  HomeAdditions: [
    buildQuestion('additionSize', 'How large will the addition be?', [
      'Under 200 sq ft',
      '200\u2013500 sq ft',
      '500+ sq ft',
    ]),
    buildQuestion('additionType', 'What type of addition?', [
      'Bedroom / living space',
      'Kitchen extension',
      'Second story addition',
    ]),
    buildQuestion('plansReady', 'Do you already have plans?', [
      'Yes, plans ready',
      'Working with architect',
      'Just exploring ideas',
    ]),
  ],
};
const DEFAULT_QUESTION_CONFIG = [
  buildQuestion('projectSize', 'What size is your project?', [
    'Small',
    'Medium',
    'Large',
  ]),
  buildQuestion('projectScope', 'How extensive is the work?', [
    'Basic refresh',
    'Moderate updates',
    'Full remodel',
  ], false),
  buildQuestion('planningStage', 'How far along are you in planning?', [
    'Plans ready',
    'Gathering estimates',
    'Just exploring',
  ]),
];

let activePostLocationStepIndex = null;
let postLocationTransitionTimeoutId;
let categoryTransitionTimeoutId;
let analysisTimerIds = [];

function getCategoryQuestions(category) {
  return CATEGORY_QUESTION_CONFIG[category] || DEFAULT_QUESTION_CONFIG;
}

function clearPostLocationTransitionTimeout() {
  if (postLocationTransitionTimeoutId) {
    window.clearTimeout(postLocationTransitionTimeoutId);
    postLocationTransitionTimeoutId = null;
  }
}

function clearCategoryTransitionTimeout() {
  if (categoryTransitionTimeoutId) {
    window.clearTimeout(categoryTransitionTimeoutId);
    categoryTransitionTimeoutId = null;
  }
}

function clearQuestionAnswers(startIndex = 0) {
  selectedQuestionAnswers = selectedQuestionAnswers.slice(0, startIndex);
}

function showWizardProgress(currentStep, totalSteps = WIZARD_PROGRESS_TOTAL_STEPS) {
  const fillPercent = Math.max(0, Math.min(100, ((currentStep - 1) / totalSteps) * 100));

  wizardShell.classList.add('wizard-shell-steady');
  wizardProgress.classList.remove('hide');
  wizardProgress.setAttribute('aria-hidden', 'false');
  wizardProgressLabel.textContent = `Step ${currentStep} of ${totalSteps}`;
  wizardProgressFill.style.width = `${fillPercent}%`;
}

function hideWizardProgress(keepShellSteady = false) {
  if (!keepShellSteady) {
    wizardShell.classList.remove('wizard-shell-steady');
  }
  wizardProgress.classList.add('hide');
  wizardProgress.setAttribute('aria-hidden', 'true');
}

function updateWizardProgressForPostLocationStep(stepIndex) {
  if (stepIndex > LAST_PROGRESS_STEP_INDEX) {
    hideWizardProgress();
    return;
  }

  showWizardProgress(stepIndex + 1);
}

function clearAnalysisSequence() {
  analysisTimerIds.forEach((timerId) => {
    window.clearTimeout(timerId);
  });
  analysisTimerIds = [];
  analysisItems.forEach((item) => {
    item.classList.remove('visible');
  });
}

function forceReflow(element) {
  return element.offsetWidth;
}

function resetQuoteFormStatus() {
  quoteFormStatus.textContent = '';
  quoteFormStatus.classList.add('hide');
  quoteFormStatus.classList.remove('quote-form-status-error');
}

function setQuoteFormStatus(message) {
  quoteFormStatus.textContent = message;
  quoteFormStatus.classList.remove('hide');
  quoteFormStatus.classList.add('quote-form-status-error');
}

function resetQuoteForm() {
  quoteForm.reset();
  isSubmittingQuote = false;
  submitQuoteButton.textContent = 'Get My Quote';
  resetQuoteFormStatus();
  updateQuoteSubmitState();
}

function resetQuestionFlow() {
  clearCategoryTransitionTimeout();
  clearPostLocationTransitionTimeout();
  activePostLocationStepIndex = null;
  wizardShell.classList.remove('wizard-shell-steady');
  clearQuestionAnswers();
  clearAnalysisSequence();
  postLocationContainers.forEach((container) => {
    container.classList.remove('animate');
    container.classList.remove('to-left');
    container.classList.remove('to-right');
    container.classList.add('hide');
  });
}

function finalizePostLocationStep(stepIndex) {
  postLocationContainers.forEach((container, index) => {
    container.classList.remove('animate');

    if (index === stepIndex) {
      container.classList.remove('hide');
      container.classList.remove('to-left');
      container.classList.remove('to-right');
      container.classList.add('animate');
      return;
    }

    container.classList.add('hide');

    if (index < stepIndex) {
      container.classList.add('to-left');
      container.classList.remove('to-right');
      return;
    }

    container.classList.add('to-right');
    container.classList.remove('to-left');
  });
}

function queuePostLocationStepFinalization(stepIndex) {
  clearPostLocationTransitionTimeout();
  postLocationTransitionTimeoutId = window.setTimeout(() => {
    finalizePostLocationStep(stepIndex);
    postLocationTransitionTimeoutId = null;
  }, STEP_TRANSITION_MS);
}

function setInitialPostLocationStep(stepIndex) {
  const activeContainer = postLocationContainers[stepIndex];

  updateWizardProgressForPostLocationStep(stepIndex);

  postLocationContainers.forEach((container, index) => {
    container.classList.remove('animate');

    if (index === stepIndex) {
      container.classList.remove('hide');
      container.classList.remove('to-left');
      container.classList.add('to-right');
      return;
    }

    container.classList.add('hide');

    if (index < stepIndex) {
      container.classList.add('to-left');
      container.classList.remove('to-right');
      return;
    }

    container.classList.add('to-right');
    container.classList.remove('to-left');
  });

  forceReflow(activeContainer);
  activeContainer.classList.remove('to-right');
  activeContainer.classList.add('animate');
  activePostLocationStepIndex = stepIndex;
  queuePostLocationStepFinalization(stepIndex);
}

function setActivePostLocationStep(stepIndex) {
  if (activePostLocationStepIndex === null) {
    setInitialPostLocationStep(stepIndex);
    return;
  }

  if (activePostLocationStepIndex === stepIndex) {
    updateWizardProgressForPostLocationStep(stepIndex);
    finalizePostLocationStep(stepIndex);
    return;
  }

  const currentStepIndex = activePostLocationStepIndex;
  const currentContainer = postLocationContainers[currentStepIndex];
  const nextContainer = postLocationContainers[stepIndex];
  const movingBackward = stepIndex < currentStepIndex;

  clearPostLocationTransitionTimeout();
  updateWizardProgressForPostLocationStep(stepIndex);

  nextContainer.classList.remove('hide');
  nextContainer.classList.remove('animate');
  nextContainer.classList.remove(movingBackward ? 'to-right' : 'to-left');
  nextContainer.classList.add(movingBackward ? 'to-left' : 'to-right');

  currentContainer.classList.remove('hide');
  currentContainer.classList.remove('to-left');
  currentContainer.classList.remove('to-right');
  currentContainer.classList.add('animate');

  forceReflow(nextContainer);

  currentContainer.classList.remove('animate');
  currentContainer.classList.add(movingBackward ? 'to-right' : 'to-left');
  nextContainer.classList.remove(movingBackward ? 'to-left' : 'to-right');
  nextContainer.classList.add('animate');

  activePostLocationStepIndex = stepIndex;
  queuePostLocationStepFinalization(stepIndex);
}

function transitionToCategoryStep() {
  if (activePostLocationStepIndex === null) {
    hideWizardProgress();
    categoryContainer.classList.remove('hide');
    categoryContainer.classList.remove('animate');
    return;
  }

  const currentContainer = postLocationContainers[activePostLocationStepIndex];

  clearCategoryTransitionTimeout();
  clearPostLocationTransitionTimeout();
  clearAnalysisSequence();
  clearQuestionAnswers();
  clearText(locationSearch);
  selectedSearch = undefined;
  selectedCity = undefined;
  selectedState = undefined;
  activePostLocationStepIndex = null;
  hideWizardProgress(true);

  categoryContainer.classList.remove('hide');
  categoryContainer.classList.remove('to-left');
  categoryContainer.classList.remove('to-right');
  categoryContainer.classList.add('animate');

  currentContainer.classList.remove('hide');
  currentContainer.classList.remove('to-left');
  currentContainer.classList.remove('to-right');
  currentContainer.classList.add('animate');

  forceReflow(categoryContainer);

  currentContainer.classList.remove('animate');
  currentContainer.classList.add('to-right');
  categoryContainer.classList.remove('animate');

  categoryTransitionTimeoutId = window.setTimeout(() => {
    categoryTransitionTimeoutId = null;
    resetQuestionFlow();
    resetQuoteForm();
  }, STEP_TRANSITION_MS);
}

function renderQuestionStep(stepIndex) {
  const questionStep = questionSteps[stepIndex];
  const question = getCategoryQuestions(selectedCategory)[stepIndex];

  questionStep.heading.textContent = question.question;
  questionStep.buttonContainer.setAttribute('aria-label', question.question);
  questionStep.buttonContainer.innerHTML = '';

  question.options.forEach((option) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-hyponia btn-pro btn-time';
    button.dataset.optionValue = option.value;
    button.dataset.optionLabel = option.label;
    button.textContent = option.label;
    questionStep.buttonContainer.appendChild(button);
  });
}

function renderQuestionSteps() {
  questionSteps.forEach((_, index) => {
    renderQuestionStep(index);
  });
}

function isQuoteFormValid() {
  const firstName = firstNameInput.value.trim();
  const lastName = lastNameInput.value.trim();
  const phoneNumber = phoneNumberInput.value.trim();
  const emailAddress = emailAddressInput.value.trim();
  const isPhoneNumeric = /^[0-9]+$/.test(phoneNumber);
  const isEmailValid = emailRegex.test(emailAddress);

  return firstName !== ''
    && lastName !== ''
    && phoneNumber !== ''
    && isPhoneNumeric
    && emailAddress !== ''
    && isEmailValid;
}

function updateQuoteSubmitState() {
  submitQuoteButton.disabled = isSubmittingQuote || !isQuoteFormValid();
}

function buildQuoteSubmissionPayload() {
  return {
    category: selectedCategory,
    formattedAddress: selectedSearch,
    city: selectedCity,
    stateCode: selectedState,
    firstName: firstNameInput.value.trim(),
    lastName: lastNameInput.value.trim(),
    phoneNumber: phoneNumberInput.value.trim(),
    emailAddress: emailAddressInput.value.trim(),
    answers: selectedQuestionAnswers.filter(Boolean),
  };
}

async function getQuoteSubmissionError(response) {
  const responseJson = response.clone();
  const responseText = response.clone();

  try {
    const errorData = await responseJson.json();
    if (typeof errorData?.message === 'string' && errorData.message.trim() !== '') {
      return `Request failed (${response.status} ${response.statusText}): ${errorData.message}`;
    }
  } catch (error) {
    // Fall through to text parsing when the response is not JSON.
  }

  try {
    const errorText = (await responseText.text()).trim();
    if (errorText !== '') {
      return `Request failed (${response.status} ${response.statusText}): ${errorText}`;
    }
  } catch (error) {
    // Fall through to the generic message when the response body cannot be read.
  }

  return `Request failed (${response.status} ${response.statusText}).`;
}

async function submitQuoteRequest() {
  const payload = buildQuoteSubmissionPayload();
  console.info('Submitting remodel quote', {
    url: remodelQuoteSubmissionUrl,
    payload,
  });

  let response;

  try {
    response = await fetch(remodelQuoteSubmissionUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('Network error calling remodel quote endpoint', {
      url: remodelQuoteSubmissionUrl,
      error,
    });

    throw new Error(`Network error calling ${remodelQuoteSubmissionUrl}. Check that the API is running, the HTTPS certificate is trusted, and CORS allows this origin.`);
  }

  console.info('Remodel quote response received', {
    url: remodelQuoteSubmissionUrl,
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  });

  if (!response.ok) {
    const errorMessage = await getQuoteSubmissionError(response);
    console.error('Remodel quote submission failed', {
      url: remodelQuoteSubmissionUrl,
      status: response.status,
      statusText: response.statusText,
      message: errorMessage,
    });
    throw new Error(errorMessage);
  }

  try {
    const responseBody = await response.clone().json();
    console.info('Remodel quote response body', responseBody);
  } catch (error) {
    console.info('Remodel quote response did not contain JSON.');
  }
}

function startAnalysisSequence() {
  clearAnalysisSequence();

  analysisItems.forEach((item, index) => {
    const timerId = window.setTimeout(() => {
      item.classList.add('visible');
    }, ANALYSIS_ITEM_DELAY_MS * (index + 1));

    analysisTimerIds.push(timerId);
  });

  const transitionDelay = (ANALYSIS_ITEM_DELAY_MS * analysisItems.length) + ANALYSIS_FINAL_DELAY_MS;
  const finalTimerId = window.setTimeout(() => {
    analysisTimerIds = [];
    setActivePostLocationStep(QUOTE_FORM_STEP_INDEX);
    updateQuoteSubmitState();
  }, transitionDelay);

  analysisTimerIds.push(finalTimerId);
}

function beginQuestionFlow() {
  clearQuestionAnswers();
  clearAnalysisSequence();
  renderQuestionSteps();
  setActivePostLocationStep(0);
  resetQuoteForm();
}

function handleQuestionSelection(stepIndex, selectedOptionButton) {
  const question = getCategoryQuestions(selectedCategory)[stepIndex];

  selectedQuestionAnswers[stepIndex] = {
    key: question.key,
    question: question.question,
    value: selectedOptionButton.getAttribute('data-option-value'),
    answer: selectedOptionButton.getAttribute('data-option-label'),
  };

  clearQuestionAnswers(stepIndex + 1);

  if (stepIndex === questionSteps.length - 1) {
    clearText(locationSearch);
    selectedSearch = undefined;
    selectedCity = undefined;
    selectedState = undefined;
    setActivePostLocationStep(LOCATION_STEP_INDEX);
    return;
  }

  setActivePostLocationStep(stepIndex + 1);
}

if (proLanding) {
  categoryContainer.classList.add('hide');
  beginQuestionFlow();
}

document.getElementById('button_container').addEventListener('click', (event) => {
  const selectedCategoryLabel = event.target.closest('label[for]');

  if (selectedCategoryLabel === null) {
    return;
  }

  const selectedCategoryInput = document.getElementById(selectedCategoryLabel.getAttribute('for'));
  selectedCategoryInput.checked = true;
  selectedCategory = selectedCategoryInput.getAttribute('data-id');

  const whereHeader = document.getElementById('where_header');
  whereHeader.textContent = 'Select your address from the dropdown';

  clearCategoryTransitionTimeout();
  categoryContainer.classList.remove('hide');
  beginQuestionFlow();
  categoryContainer.classList.add('animate');

  categoryTransitionTimeoutId = window.setTimeout(() => {
    categoryContainer.classList.add('hide');
    categoryTransitionTimeoutId = null;
  }, STEP_TRANSITION_MS);
});

backButton.addEventListener('click', () => {
  setActivePostLocationStep(questionSteps.length - 1);
});

backToLocationButton.addEventListener('click', () => {
  transitionToCategoryStep();
});

questionSteps.forEach((questionStep, index) => {
  questionStep.buttonContainer.addEventListener('click', (event) => {
    const selectedOptionButton = event.target.closest('button[data-option-value]');
    if (selectedOptionButton === null) {
      return;
    }

    handleQuestionSelection(index, selectedOptionButton);
  });
});

backToQuestionStepOneButton.addEventListener('click', () => {
  clearQuestionAnswers(1);
  setActivePostLocationStep(0);
});

backToQuestionStepTwoButton.addEventListener('click', () => {
  clearQuestionAnswers(2);
  setActivePostLocationStep(1);
});

phoneNumberInput.addEventListener('input', () => {
  phoneNumberInput.value = phoneNumberInput.value.replace(/\D/g, '');
  resetQuoteFormStatus();
  updateQuoteSubmitState();
});

[firstNameInput, lastNameInput, emailAddressInput].forEach((input) => {
  input.addEventListener('input', () => {
    resetQuoteFormStatus();
    updateQuoteSubmitState();
  });
});

quoteForm.addEventListener('submit', async (event) => {
  event.preventDefault();

  if (isSubmittingQuote || !isQuoteFormValid()) {
    updateQuoteSubmitState();
    return;
  }

  isSubmittingQuote = true;
  submitQuoteButton.textContent = 'Submitting...';
  resetQuoteFormStatus();
  updateQuoteSubmitState();

  try {
    await submitQuoteRequest();
    setActivePostLocationStep(SUBMISSION_SUCCESS_STEP_INDEX);
  } catch (error) {
    console.error(error);
    setQuoteFormStatus(error.message || 'We could not submit your information. Please try again.');
  } finally {
    isSubmittingQuote = false;
    submitQuoteButton.textContent = 'Get My Quote';
    updateQuoteSubmitState();
  }
});

const loader = new Loader({
  apiKey: 'AIzaSyDPRMuc-vQprevDs0T98KYsk2Wj_ytM0gM',
  libraries: ['places'],
  region: 'us',
});

loader
  .load()
  .then(() => {
    const options = {
      componentRestrictions: { country: ['us'] },
      types: ['geocode'],
    };
    const autocomplete = new google.maps.places.Autocomplete(locationSearch, options);
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const radioChecked = document.querySelector('input[name=radio-category]:checked');
      if (radioChecked) {
        selectedCategory = radioChecked.getAttribute('data-id');
      }

      const place = autocomplete.getPlace();
      selectedSearch = place.formatted_address;
      let city; let
        state;

      place.address_components.forEach((component) => {
        if (component.types.includes('sublocality') || component.types.includes('locality')) {
          city = component.short_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
      });

      selectedCity = city;
      selectedState = state;
      setActivePostLocationStep(ANALYSIS_STEP_INDEX);
      startAnalysisSequence();
    });
  })
  .catch((e) => {
    console.log(e);
  });
